import React from 'react';
import { useTranslations } from '@/hooks/use-translations';
import { useLocale } from '@/components/locale-provider';

interface SEOProps {
  title?: string;
  description?: string;
  ogImage?: string;
  url?: string;
  type?: string;
  noIndex?: boolean;
}

const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  ogImage = 'https://28x.com/assets/og-image.jpg', // Replace with your actual OG image URL
  url = 'https://28x.com', // Replace with your domain
  type = 'website',
  noIndex = false,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslations(locale);
  
  // Use provided values or fallbacks from translations
  const seoTitle = title || t('home').seo.title;
  const seoDescription = description || t('home').seo.description;
  
  return (
    <>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      
      {/* No index if required */}
      {noIndex && <meta name="robots" content="noindex" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={ogImage} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={seoTitle} />
      <meta property="twitter:description" content={seoDescription} />
      <meta property="twitter:image" content={ogImage} />
      
      {/* Preload Fonts */}
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      
      {/* Preload critical assets */}
      <link rel="preload" href="/assets/upper-left-28x-logo.svg" as="image" />
      
      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '28X',
            description: 'Privacy-focused period tracking app',
            url: 'https://28x.com'
          })
        }}
      />
    </>
  );
};

export default SEOHead;
